import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Strip a single outer `<imported_field>…</imported_field>` wrapper from
 * presentation text (ISS-408). The wrapper is an internal anti prompt-
 * injection marker (LLM-consumed content) and must never surface in
 * user-facing fields such as descriptions.
 *
 * Handles both stored forms: raw (`<imported_field>…</imported_field>`) and
 * html-escaped by the schema validator (`&lt;imported_field&gt;…`).
 */
export function stripImportedField(text: string | null | undefined): string {
  if (!text) return text ?? '';
  const s = text.trim();
  const rawOpen = '<imported_field>';
  const escOpen = '&lt;imported_field&gt;';
  const rawClose = '</imported_field>';
  const escClose = '&lt;/imported_field&gt;';

  const open = s.startsWith(escOpen)
    ? escOpen
    : s.startsWith(rawOpen)
      ? rawOpen
      : null;
  const close = s.endsWith(escClose)
    ? escClose
    : s.endsWith(rawClose)
      ? rawClose
      : null;
  if (!open || !close) return text;

  let inner = s.slice(open.length, -close.length).trim();
  if (open === escOpen || close === escClose) {
    inner = inner
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'");
  }
  return inner;
}

/**
 * Generate a unique ID for client-side usage
 */
export function generateId(prefix: string = 'id'): string {
  return `${prefix}_${Math.random().toString(36).substring(2, 14)}`;
}

/**
 * Format date for display
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(d);
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Format number with thousand separators
 */
export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null) return '0';
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Format large numbers with K/M/B suffixes
 */
export function formatCompactNumber(value: number | undefined | null): string {
  if (value === undefined || value === null) return '0';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format currency in USD
 */
export function formatCurrency(
  value: number | string | undefined | null,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  if (value === undefined || value === null) return '$0.00';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) return '$0.00';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 6,
  }).format(numValue);
}

/**
 * Format percentage
 */
export function formatPercent(value: number | undefined | null): string {
  if (value === undefined || value === null) return '0%';
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}
